import { memo, useCallback, useMemo, useReducer, useState } from 'react'

import { ArrowCircleRight, Cancel, Logout } from '@mui/icons-material'
import { Alert, AlertTitle, Box, Button, Divider, Typography } from '@mui/material'
import { upperFirst } from 'lodash-es'
import { AnimatePresence } from 'motion/react'

import { ErrorBoundary } from '@shared/components/ErrorHandling/ErrorBoundary'
import { Sidebar, Structure } from '@shared/layouts'
import {
  InvitationContext,
  InvitationContextValue,
  DynamicFlowContent,
  reducer,
  State,
} from 'domains/ProcessInvitation'
import { processInvitationRoute } from 'pages/AppInit/Routing'

const validProceedStates: State[] = [
  'PARAMETERS_INVALID',
  'ACCOUNT_AVAILABLE',
  'ACCOUNT_MISSING',
  'MEMBER_ASSOCIATED',
  'MEMBER_ALREADY_ASSOCIATED',
] as const
const validSignOutStates: State[] = ['ACCOUNT_AVAILABLE', 'MEMBER_NOT_ALLOWED_TO_BE_ASSOCIATED'] as const
const validCancelStates: State[] = [
  'PARAMETERS_INVALID',
  'ACCOUNT_AVAILABLE',
  'ACCOUNT_MISSING',
  'IDENTITY_SIGN_IN_FAILED',
  'INVITATION_DETAIL_FAILED',
  'MEMBER_NOT_ALLOWED_TO_ACCESS',
  'MEMBER_NOT_ALLOWED_TO_BE_ASSOCIATED',
  'MEMBER_ASSOCIATION_FAILED',
] as const

const RootRaw = () => {
  const [state, dispatch] = useReducer(reducer, 'START')

  const { origin } = processInvitationRoute.useSearch()

  const [finiteStateTrace, setFiniteStateTrace] = useState<State[]>([])

  const handleAddFiniteState = useCallback((state: State) => {
    setFiniteStateTrace((prev) => [...prev, state])
  }, [])

  const contextValue = useMemo<InvitationContextValue>(
    () => [state, dispatch, finiteStateTrace, handleAddFiniteState],
    [finiteStateTrace, handleAddFiniteState, state],
  )

  const derivedCancelDisabled = !validCancelStates.includes(state)

  const derivedProceedDisabled = !validProceedStates.includes(state)
  const derivedSignOutDisabled = !validSignOutStates.includes(state)

  return (
    <Structure
      pinned={false}
      SidebarSlot={
        <Sidebar pinned={false}>
          <Typography sx={{ mx: 2, my: 2.5 }} variant="heading-xl">
            NW Console
          </Typography>
          <Divider sx={{ borderWidth: 4 }} />
          <Alert sx={{ mx: 1, mt: 2 }} severity="info">
            <AlertTitle>Invitation</AlertTitle>

            <Typography variant="inherit" component="span">
              An Invitation process is currently ongoing. Please finish the process to continue.
            </Typography>
          </Alert>
        </Sidebar>
      }
    >
      <InvitationContext.Provider value={contextValue}>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Box sx={{ maxWidth: 540, width: '100%' }}>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="heading-xl">{upperFirst(origin)} Invitation</Typography>
              <Button
                variant="text"
                endIcon={<Cancel />}
                onClick={() => dispatch('CANCEL')}
                disabled={derivedCancelDisabled}
              >
                Cancel
              </Button>
            </Box>

            <ErrorBoundary>
              <AnimatePresence mode="wait">
                <DynamicFlowContent />
              </AnimatePresence>
            </ErrorBoundary>

            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', columnGap: 2 }}>
              <Button
                variant="outlined"
                endIcon={<Logout />}
                onClick={() => dispatch('SIGN_OUT')}
                disabled={derivedSignOutDisabled}
              >
                Sign-Out
              </Button>

              <Button
                variant="contained"
                endIcon={<ArrowCircleRight />}
                onClick={() => dispatch('PROCEED')}
                disabled={derivedProceedDisabled}
              >
                Proceed
              </Button>
            </Box>
          </Box>
        </Box>
      </InvitationContext.Provider>
    </Structure>
  )
}

export const Root = memo(RootRaw)
